import { useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useParams } from 'react-router-dom';

import { Button, Input, Typography } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import formatDistanceStrict from 'date-fns/formatDistanceStrict';
import ptBR from 'date-fns/locale/pt-BR';
import { QRCodeSVG } from 'qrcode.react';
import GlobalLoading from 'v2/components/Shared/GlobalLoading';
import UnloggedLayout from 'v2/components/Shared/UnloggedLayout';
import { numberToBRL } from 'v2/helpers/money';
import { useAutomaticPixAuthorization } from 'v2/hooks/useAutomaticPixAuthorization';

const COPY_FEEDBACK_MS = 3000;

const INTERVAL_LABELS: Record<string, string> = {
  month: 'mensalmente',
  year: 'anualmente'
};

type PixAutomaticParams = {
  id: string;
};

function formatExpiresInLabel(expiresAt: string): string {
  const expiresDate = new Date(expiresAt);
  const now = new Date();

  if (expiresDate.getTime() <= now.getTime()) {
    return 'expirado';
  }

  return formatDistanceStrict(expiresDate, now, { locale: ptBR });
}

function formatIntervalLabel(interval: string): string {
  return INTERVAL_LABELS[interval] ?? interval;
}

function InvalidLinkMessage() {
  return (
    <div className='mx-auto flex min-h-[70vh] flex-col items-center justify-center p-4 text-center'>
      <Icon.TriangleAlert className='text-secondary-600 mb-4 text-[4rem]' />
      <Typography.Title level={4}>Link inválido</Typography.Title>
      <Typography.Paragraph>Parâmetros inválidos. Verifique o link recebido.</Typography.Paragraph>
    </div>
  );
}

function ErrorMessage({ message }: { message: string }) {
  return (
    <div className='mx-auto flex min-h-[70vh] flex-col items-center justify-center p-4 text-center'>
      <Icon.TriangleAlert className='text-secondary-600 mb-4 text-[4rem]' />
      <Typography.Title level={4}>Não foi possível carregar</Typography.Title>
      <Typography.Paragraph>{message}</Typography.Paragraph>
    </div>
  );
}

function PixAutomaticContent() {
  const { id } = useParams<PixAutomaticParams>();
  const [didCopy, setDidCopy] = useState(false);
  const { data, isLoading, isError } = useAutomaticPixAuthorization(id);

  if (!id) {
    return <InvalidLinkMessage />;
  }

  if (isLoading) {
    return <GlobalLoading />;
  }

  if (isError || !data?.barcode) {
    return <ErrorMessage message='Não foi possível carregar os dados do Pix Automático. Tente novamente mais tarde.' />;
  }

  const copyPasteCode = data.barcode;
  const amountLabel = numberToBRL(data.amountCents / 100);
  const intervalLabel = formatIntervalLabel(data.interval);
  const expiresInLabel = formatExpiresInLabel(data.expiresAt);
  const isExpired = expiresInLabel === 'expirado';

  if (isExpired) {
    return <ErrorMessage message='Este código Pix Automático expirou. Solicite um novo link de autorização.' />;
  }

  const handleCopy = () => {
    setDidCopy(true);
    setTimeout(() => setDidCopy(false), COPY_FEEDBACK_MS);
  };

  return (
    <div className='mx-auto flex max-w-160 flex-col items-center'>
      <div className='mb-6 flex w-full flex-col items-center text-center'>
        <Typography.Title level={3} className='mb-2!'>
          Ativar Pix Automático
        </Typography.Title>
        <Typography.Paragraph className='text-content-subtitle mb-0!'>
          Escaneie o QR Code ou copie o código Pix para autorizar a cobrança automática {intervalLabel} de{' '}
          {amountLabel}.
        </Typography.Paragraph>
        <Typography.Text type='secondary' className='mt-2 text-sm'>
          Código válido por {expiresInLabel}
        </Typography.Text>
      </div>

      <div className='border-outline-default bg-surface-default mb-8 flex items-center justify-center rounded-sm border p-4'>
        <QRCodeSVG value={copyPasteCode} size={200} />
      </div>

      <div className='mb-4 flex w-full flex-col'>
        <Typography.Title level={5} className='mb-1!'>
          Pix Copia e Cola
        </Typography.Title>
        <Typography.Paragraph className='text-content-subtitle mb-4! text-sm'>
          No app do seu banco, selecione Pix Copia e Cola, cole o código abaixo e finalize a autorização.
        </Typography.Paragraph>

        <Typography.Text className='text-content-subtitle mb-1 text-sm'>Código de pagamento</Typography.Text>
        <Input type='text' value={copyPasteCode} readOnly />

        <CopyToClipboard text={copyPasteCode} onCopy={handleCopy}>
          <Button className='mt-2!' icon={didCopy ? <Icon.Check /> : <Icon.Copy />} htmlType='button' block>
            {didCopy ? 'Código copiado!' : 'Copiar código Pix'}
          </Button>
        </CopyToClipboard>
      </div>

      <ol className='text-content-subtitle mt-4! mb-0! w-full list-none space-y-1 text-sm'>
        <li>
          <Typography.Text strong>1.</Typography.Text> Abra o app do seu banco;
        </li>
        <li>
          <Typography.Text strong>2.</Typography.Text> Escolha pagar via Pix (QR Code ou Copia e Cola);
        </li>
        <li>
          <Typography.Text strong>3.</Typography.Text> Autorize a cobrança automática e confirme.
        </li>
      </ol>
    </div>
  );
}

export function PixAutomatic() {
  return (
    <UnloggedLayout>
      <PixAutomaticContent />
    </UnloggedLayout>
  );
}

export default PixAutomatic;
